package com.example.socialmediaapp.navigation

import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.navigation.NavType
import androidx.navigation.compose.*
import androidx.navigation.navArgument
import com.example.socialmediaapp.TokenStorage
import com.example.socialmediaapp.data.models.*
import com.example.socialmediaapp.network.AdminRepository
import com.example.socialmediaapp.network.ApiClient
import com.example.socialmediaapp.network.AuthRepository
import com.example.socialmediaapp.network.CallRepository
import com.example.socialmediaapp.network.ChatRepository
import com.example.socialmediaapp.network.ChatWebSocketClient
import com.example.socialmediaapp.network.FeedRepository
import com.example.socialmediaapp.network.MarketplaceRepository
import com.example.socialmediaapp.network.NotificationsRepository
import com.example.socialmediaapp.network.PostRepository
import com.example.socialmediaapp.network.StoriesRepository
import com.example.socialmediaapp.ui.components.OnboardingTagScreen
import com.example.socialmediaapp.ui.screens.*
import kotlinx.coroutines.launch

// ============================================
// SCREEN ROUTES
// ============================================

sealed class Screen(val route: String) {
    object Login : Screen("login")
    object Register : Screen("register")
    object Feed : Screen("feed")
    object ChatList : Screen("chat_list")
    object ChatDetail : Screen("chat_detail/{chatId}") {
        fun createRoute(chatId: String) = "chat_detail/$chatId"
    }
    object NewChat : Screen("new_chat")
    object Profile : Screen("profile")
    object Settings : Screen("settings")
    object Search : Screen("search")
    object Marketplace : Screen("marketplace")
    object Notifications : Screen("notifications")
    object BusinessProfile : Screen("business_profile/{businessId}") {
        fun createRoute(businessId: String) = "business_profile/$businessId"
    }
    object Onboarding : Screen("onboarding")
    object Call : Screen("call/{chatId}/{chatName}/{isVideo}") {
        fun createRoute(chatId: String, chatName: String, isVideo: Boolean) = "call/$chatId/$chatName/$isVideo"
    }
    object Dashboard : Screen("dashboard")
}

// ============================================
// APP NAVIGATION
// ============================================

@Composable
fun AppNavigation(tokenStorage: TokenStorage) {
    val navController = rememberNavController()
    val platform = remember { com.example.socialmediaapp.getPlatform() }
    var isLoggedIn by remember { mutableStateOf(tokenStorage.getToken() != null && tokenStorage.getToken() != "guest-token") }
    val isGuest = remember(isLoggedIn, tokenStorage.getToken()) { !isLoggedIn || tokenStorage.getToken() == "guest-token" }

    val apiClient = remember { ApiClient(tokenStorage) }
    val authRepo = remember { AuthRepository(apiClient) }
    val feedRepo = remember { FeedRepository(apiClient) }
    val chatRepo = remember { ChatRepository(apiClient) }
    val notificationsRepo = remember { NotificationsRepository(apiClient) }
    val marketplaceRepo = remember { MarketplaceRepository(apiClient) }
    val storiesRepo = remember { StoriesRepository(apiClient) }
    val postRepo = remember { PostRepository(apiClient) }
    val callRepo = remember { CallRepository(apiClient) }
    val adminRepo = remember { AdminRepository(apiClient) }
    val wsClient = remember { ChatWebSocketClient(chatRepo) }
    val scope = rememberCoroutineScope()

    // Connect WebSocket when logged in, disconnect on logout
    LaunchedEffect(isLoggedIn) {
        if (isLoggedIn) {
            val token = tokenStorage.getToken() ?: return@LaunchedEffect
            wsClient.connect(token, scope)
        } else {
            wsClient.disconnect()
        }
    }

    // Auth loading/error state
    var isLoggingIn by remember { mutableStateOf(false) }
    var loginError by remember { mutableStateOf<String?>(null) }
    var isRegistering by remember { mutableStateOf(false) }
    var registerError by remember { mutableStateOf<String?>(null) }

    val currentBackStack by navController.currentBackStackEntryAsState()
    val currentRoute = currentBackStack?.destination?.route

    val mainRoutes = setOf(
        Screen.Feed.route,
        Screen.ChatList.route,
        Screen.Marketplace.route,
        Screen.Notifications.route,
        Screen.Profile.route
    )
    val showBottomNav = (isLoggedIn || currentRoute == Screen.Feed.route || currentRoute == Screen.Marketplace.route) && currentRoute in mainRoutes

    Scaffold(
        bottomBar = {
            if (showBottomNav) {
                NavigationBar {
                    NavigationBarItem(
                        icon = {
                            Icon(
                                if (currentRoute == Screen.Feed.route) Icons.Default.Home
                                else Icons.Outlined.Home,
                                "Home"
                            )
                        },
                        label = { Text("Home") },
                        selected = currentRoute == Screen.Feed.route,
                        onClick = {
                            navController.navigate(Screen.Feed.route) {
                                popUpTo(navController.graph.startDestinationId)
                                launchSingleTop = true
                            }
                        }
                    )
                    NavigationBarItem(
                        icon = {
                            Icon(
                                if (currentRoute == Screen.ChatList.route) Icons.Default.Chat
                                else Icons.Outlined.Chat,
                                "Chats"
                            )
                        },
                        label = { Text("Chats") },
                        selected = currentRoute == Screen.ChatList.route,
                        onClick = {
                            navController.navigate(Screen.ChatList.route) {
                                popUpTo(navController.graph.startDestinationId)
                                launchSingleTop = true
                            }
                        }
                    )
                    NavigationBarItem(
                        icon = {
                            Icon(
                                if (currentRoute == Screen.Marketplace.route) Icons.Default.ShoppingBag
                                else Icons.Outlined.ShoppingBag,
                                "Market"
                            )
                        },
                        label = { Text("Market") },
                        selected = currentRoute == Screen.Marketplace.route,
                        onClick = { navController.navigate(Screen.Marketplace.route) { launchSingleTop = true } }
                    )
                    NavigationBarItem(
                        icon = {
                            Icon(
                                if (currentRoute == Screen.Notifications.route) Icons.Default.Notifications
                                else Icons.Outlined.Notifications,
                                "Alerts"
                            )
                        },
                        label = { Text("Alerts") },
                        selected = currentRoute == Screen.Notifications.route,
                        onClick = { navController.navigate(Screen.Notifications.route) { launchSingleTop = true } }
                    )
                    NavigationBarItem(
                        icon = {
                            Icon(
                                if (currentRoute == Screen.Profile.route) Icons.Default.Person
                                else Icons.Outlined.Person,
                                "Profile"
                            )
                        },
                        label = { Text("Profile") },
                        selected = currentRoute == Screen.Profile.route,
                        onClick = { navController.navigate(Screen.Profile.route) { launchSingleTop = true } }
                    )
                }
            }
        }
    ) { innerPadding ->
        NavHost(
            navController = navController,
            startDestination = if (platform.isAdminDomain) Screen.Dashboard.route else Screen.Feed.route,
            modifier = Modifier.padding(innerPadding)
        ) {
            // Auth
            composable(Screen.Login.route) {
                LoginScreen(
                    isLoading = isLoggingIn,
                    serverError = loginError,
                    onLogin = { email, password ->
                        scope.launch {
                            isLoggingIn = true
                            loginError = null
                            authRepo.login(email, password).fold(
                                onSuccess = { response ->
                                    tokenStorage.saveToken(response.token)
                                    tokenStorage.saveUserId(response.user.id)
                                    tokenStorage.saveUsername(response.user.username)
                                    isLoggedIn = true
                                    navController.navigate(Screen.Feed.route) {
                                        popUpTo(Screen.Login.route) { inclusive = true }
                                    }
                                },
                                onFailure = { e ->
                                    loginError = when {
                                        e.message?.contains("401") == true -> "Invalid email or password"
                                        e.message?.contains("423") == true -> "Account locked. Try again later"
                                        else -> "Login failed. Check your connection."
                                    }
                                }
                            )
                            isLoggingIn = false
                        }
                    },
                    onNavigateToRegister = {
                        loginError = null
                        navController.navigate(Screen.Register.route)
                    },
                    onContinueAsGuest = {
                        tokenStorage.saveToken("guest-token")
                        tokenStorage.saveUserId("guest")
                        tokenStorage.saveUsername("Guest")
                        isLoggedIn = true
                        navController.navigate(Screen.Feed.route) {
                            popUpTo(Screen.Login.route) { inclusive = true }
                        }
                    }
                )
            }

            composable(Screen.Register.route) {
                RegisterScreen(
                    isLoading = isRegistering,
                    serverError = registerError,
                    onRegister = { username, email, password ->
                        scope.launch {
                            isRegistering = true
                            registerError = null
                            authRepo.register(username, email, password).fold(
                                onSuccess = {
                                    // Auto-login after successful registration
                                    authRepo.login(email, password).fold(
                                        onSuccess = { response ->
                                            tokenStorage.saveToken(response.token)
                                            tokenStorage.saveUserId(response.user.id)
                                            tokenStorage.saveUsername(response.user.username)
                                            isLoggedIn = true
                                            navController.navigate(Screen.Feed.route) {
                                                popUpTo(Screen.Register.route) { inclusive = true }
                                            }
                                        },
                                        onFailure = {
                                            // Registered but login failed — send to login screen
                                            navController.navigate(Screen.Login.route) {
                                                popUpTo(Screen.Register.route) { inclusive = true }
                                            }
                                        }
                                    )
                                },
                                onFailure = { e ->
                                    registerError = when {
                                        e.message?.contains("409") == true -> "Username or email already taken"
                                        else -> "Registration failed. Check your connection."
                                    }
                                }
                            )
                            isRegistering = false
                        }
                    },
                    onNavigateToLogin = {
                        registerError = null
                        navController.popBackStack()
                    }
                )
            }

            // Home Feed (primary tab)
            composable(Screen.Feed.route) {
                FeedScreen(
                    feedRepository = feedRepo,
                    storiesRepository = storiesRepo,
                    postRepository = postRepo,
                    onSearchClick = { navController.navigate(Screen.Search.route) },
                    onNotificationsClick = {
                        if (isLoggedIn) navController.navigate(Screen.Notifications.route)
                        else navController.navigate(Screen.Login.route)
                    },
                    onCreateClick = {
                        if (isLoggedIn) { /* show create sheet in FeedScreen handles this */ }
                        else navController.navigate(Screen.Login.route)
                    },
                    onAuthClick = { navController.navigate(Screen.Login.route) },
                    isGuest = isGuest
                )
            }

            // Chat List
            composable(Screen.ChatList.route) {
                ChatListScreen(
                    chatRepository = chatRepo,
                    onChatClick = { chatId ->
                        navController.navigate(Screen.ChatDetail.createRoute(chatId))
                    },
                    onNewChat = { navController.navigate(Screen.NewChat.route) },
                    onSearchClick = { navController.navigate(Screen.Search.route) },
                    onSettingsClick = { navController.navigate(Screen.Profile.route) }
                )
            }

            // Chat Detail
            composable(
                route = Screen.ChatDetail.route,
                arguments = listOf(navArgument("chatId") { type = NavType.StringType })
            ) { backStackEntry ->
                val chatId = backStackEntry.savedStateHandle.get<String>("chatId") ?: ""
                ChatDetailScreen(
                    chatId = chatId,
                    chatName = "Chat",
                    currentUserId = tokenStorage.getUserId() ?: "me",
                    chatRepository = chatRepo,
                    onBack = { navController.popBackStack() },
                    onVideoCall = {
                        navController.navigate(Screen.Call.createRoute(chatId, "Chat", true))
                    },
                    onVoiceCall = {
                        navController.navigate(Screen.Call.createRoute(chatId, "Chat", false))
                    }
                )
            }

            // New Chat
            composable(Screen.NewChat.route) {
                NewChatScreen(
                    authRepository = authRepo,
                    chatRepository = chatRepo,
                    onChatCreated = { chatId ->
                        navController.navigate(Screen.ChatDetail.createRoute(chatId)) {
                            popUpTo(Screen.NewChat.route) { inclusive = true }
                        }
                    },
                    onBack = { navController.popBackStack() }
                )
            }

            // VoIP Call
            composable(
                route = Screen.Call.route,
                arguments = listOf(
                    navArgument("chatId") { type = NavType.StringType },
                    navArgument("chatName") { type = NavType.StringType },
                    navArgument("isVideo") { type = NavType.BoolType }
                )
            ) { backStackEntry ->
                val chatId = backStackEntry.savedStateHandle.get<String>("chatId") ?: ""
                val chatName = backStackEntry.savedStateHandle.get<String>("chatName") ?: "Chat"
                val isVideo = backStackEntry.savedStateHandle.get<Boolean>("isVideo") ?: false
                CallScreen(
                    chatId = chatId,
                    chatName = chatName,
                    isVideoCall = isVideo,
                    callRepository = callRepo,
                    onEnd = { navController.popBackStack() }
                )
            }

            // Profile (includes settings + logout)
            composable(Screen.Profile.route) {
                val currentUser = User(
                    id = tokenStorage.getUserId() ?: "me",
                    username = tokenStorage.getUsername() ?: "User",
                    email = ""
                )
                ProfileScreen(
                    user = currentUser,
                    authRepository = authRepo,
                    onSave = { updatedUser ->
                        scope.launch {
                            authRepo.updateProfile(updatedUser.username, updatedUser.bio).onSuccess { saved ->
                                tokenStorage.saveUsername(saved.username)
                            }
                        }
                    },
                    onSettingsClick = { navController.navigate(Screen.Settings.route) },
                    onLogout = {
                        scope.launch {
                            authRepo.logout() // best-effort; ignore errors
                        }
                        tokenStorage.clearToken()
                        isLoggedIn = false
                        navController.navigate(Screen.Login.route) {
                            popUpTo(0) { inclusive = true }
                        }
                    }
                )
            }

            // Settings
            composable(Screen.Settings.route) {
                SettingsScreen(
                    onBack = { navController.popBackStack() },
                    onLogout = {
                        tokenStorage.clearToken()
                        isLoggedIn = false
                        navController.navigate(Screen.Login.route) {
                            popUpTo(0) { inclusive = true }
                        }
                    }
                )
            }

            // Search
            composable(Screen.Search.route) {
                SearchScreen(
                    authRepository = authRepo,
                    onBack = { navController.popBackStack() }
                )
            }

            // Marketplace
            composable(Screen.Marketplace.route) {
                MarketplaceScreen(
                    marketplaceRepository = marketplaceRepo,
                    onBack = { navController.popBackStack() }
                )
            }

            // Admin Dashboard
            composable(Screen.Dashboard.route) {
                AdminDashboardScreen(
                    adminRepository = adminRepo,
                    onBack = { if (!platform.isAdminDomain) navController.popBackStack() }
                )
            }

            // Notifications
            composable(Screen.Notifications.route) {
                NotificationsScreen(
                    notificationsRepository = notificationsRepo,
                    onBack = { navController.popBackStack() }
                )
            }

            // Business Profile
            composable(
                route = Screen.BusinessProfile.route,
                arguments = listOf(navArgument("businessId") { type = NavType.StringType })
            ) { backStackEntry ->
                val businessId = backStackEntry.savedStateHandle.get<String>("businessId") ?: ""
                val sampleProfile = BusinessProfile(
                    userId = businessId,
                    businessName = "Business Profile",
                    tagline = "Your tagline here"
                )
                BusinessProfileScreen(
                    profile = sampleProfile,
                    onBack = { navController.popBackStack() }
                )
            }

            // Onboarding (tag selection)
            composable(Screen.Onboarding.route) {
                OnboardingTagScreen(
                    onComplete = { _ ->
                        navController.navigate(Screen.Feed.route) {
                            popUpTo(Screen.Onboarding.route) { inclusive = true }
                        }
                    },
                    onSkip = {
                        navController.navigate(Screen.Feed.route) {
                            popUpTo(Screen.Onboarding.route) { inclusive = true }
                        }
                    }
                )
            }
        }
    }
}

// ============================================
// SAMPLE DATA
// ============================================

private val sampleProducts = emptyList<Product>()

private val sampleNotifications = emptyList<Notification>()
